import { ComponentProps, ComponentPropsWithoutRef, forwardRef, ReactNode } from 'react';
import {
  SelectContent,
  SelectItem as SelectItemPrimitive,
  Select as SelectRoot,
  SelectTrigger,
  SelectValue,
} from './select-primitives';

/**
 * Props for the Select component
 * @property {React.ReactNode} children - The content to be rendered inside the select dropdown
 * @property {string} placeholder - Text to display when no option is selected
 * @property {React.ReactNode} [triggerLabel] - Optional label to display above the trigger
 * @property {string} [triggerClassName] - Custom classes to style the trigger element
 * @property {string} [id] - Optional id for the select trigger
 * @property {React.ReactNode} [selectedTriggerContent] - Custom content to display in the trigger when an option is selected.
 *                                                      If not provided, the selected option's text content will be displayed.
 * @property {Object} [searchBoxConfig] - Configuration for the search box functionality
 * @property {boolean} searchBoxConfig.visible - Whether to show the search box
 * @property {React.ReactNode} [searchBoxConfig.searchBox] - Custom search box element to render when visible is true
 * @property {boolean} [isTriggerFullWidth] - Whether the select trigger should take up the full width of its container
 */
type SelectProps = ComponentPropsWithoutRef<typeof SelectRoot> & {
  children: React.ReactNode;
  placeholder: ComponentProps<typeof SelectValue>['placeholder'];
  triggerLabel?: ReactNode;
  triggerClassName?: string;
  id?: string;
  selectedTriggerContent?: ReactNode;
  searchBoxConfig?:
    | {
        visible: false;
      }
    | {
        visible: true;
        searchBox: ReactNode;
      };
  isTriggerFullWidth?: boolean;
};

/**
 * Type definition for the Select component with its Item subcomponent
 */
type SelectComponent = React.ForwardRefExoticComponent<SelectProps & React.RefAttributes<HTMLButtonElement>> & {
  Item: typeof SelectItemPrimitive;
};

/**
 * A customizable select component that provides a dropdown interface for selecting options.
 * This component is built on top of Radix UI's Select primitive (@radix-ui/react-select).
 *
 * For detailed documentation about all available features and capabilities,
 * please consult the Radix UI Select documentation:
 * @see {@link https://www.radix-ui.com/primitives/docs/components/select}
 *
 * Features include:
 * - Can be controlled or uncontrolled
 * - Supports items, labels, groups of items
 * - Full keyboard navigation
 * - Supports custom placeholder
 * - Typeahead support
 * - Customizable trigger styling via triggerClassName
 * - Optional trigger label
 * - Optional id
 * - Custom content display in trigger when selected via selectedTriggerContent
 *
 * @example
 * ```tsx
 * <Select
 *   placeholder="Select an option"
 *   triggerClassName="py-4 rounded-lg"
 *   triggerLabel="Select currency"
 *   selectedTriggerContent={<CustomSelectedDisplay />}
 * >
 *   <Select.Item value="option1">Option 1</Select.Item>
 *   <Select.Item value="option2">Option 2</Select.Item>
 * </Select>
 * ```
 */
export const Select = forwardRef<React.ElementRef<typeof SelectTrigger>, SelectProps>(
  (
    {
      children,
      placeholder,
      triggerClassName,
      triggerLabel,
      id,
      selectedTriggerContent,
      searchBoxConfig = { visible: false },
      isTriggerFullWidth = false,
      ...props
    },
    ref,
  ) => {
    const searchBox = searchBoxConfig.visible ? searchBoxConfig.searchBox : undefined;

    return (
      <SelectRoot {...props}>
        <SelectTrigger
          ref={ref}
          className={triggerClassName}
          label={triggerLabel}
          id={id}
          isFullWidth={isTriggerFullWidth}
        >
          <SelectValue placeholder={placeholder}>{selectedTriggerContent}</SelectValue>
        </SelectTrigger>

        <SelectContent searchBox={searchBox}>{children}</SelectContent>
      </SelectRoot>
    );
  },
) as SelectComponent;

/**
 * Predefined Select.Item component for use within the Select component
 */
Select.Item = SelectItemPrimitive;
